import React from 'react';
import { t } from 'i18next';
import * as Sentry from '@sentry/react';
import env from 'env';
import {
  LISTING_PACKAGE,
  LISTING_STAGE,
  LISTING_TIERS,
  LISTING_TYPE,
  LISTING_URGENT,
  Listing,
  ListingContactType,
  ListingPackage,
  ListingStage,
  ListingTier,
  ListingType,
} from 'models/Listing';
import { Params } from 'react-router-dom';
import {
  DATE_FORMAT_FULL_DATETIME,
  getFormattedDate,
  isDateFromLastDays,
} from 'dateUtils';
import {
  ASKING_PRICE_SELECT,
  Colleague,
  DEAL_STAGE,
  DealRoom,
  DealRoomTopNodes,
  DealStage,
} from 'models/DealRoom';
import { DESKTOP_APP_PLATFORM, DesktopAppPlatform } from 'models/DesktopApp';
import { LISTING_TYPE_ROUTES } from 'router/subrouters/ListingCreationWizard/utils';
import isEqual from 'lodash/isEqual';
import { KeyEvent, PastTime } from 'types';
import {
  DOLLAR,
  PERCENTRAGE,
  BUSINESS_ROUTE,
  URGENT_ROUTE,
  BUYING_PORTFOLIO_ROUTE,
  SELLING_PORTFOLIO_ROUTE,
  ENTERPRISE_ROUTE,
  DATA_ROOM_ROUTE,
  ESCAPE_KEY,
  ENTER_KEY,
  SPACE_KEY,
  ARROW_DOWN_KEY,
  ARROW_UP_KEY,
  TAB_KEY,
  SHAREABLE_LINKS_ROUTE,
  RFI_LINK,
  ARROW_LEFT_KEY,
  ARROW_RIGHT_KEY,
  MULTI_SORT_SEPARATOR,
  LCW_BASE_ROUTE,
  EDIT_DEAL_ROUTE,
  CREATE_DEAL_ROUTE,
  RWC_ROUTE,
  PRIVATE_LISTING_ROUTE,
  ADMIN_DASHBOARD_EDIT_PROFILE,
  SHEET_FILE_TYPES,
  WORD_FILES,
  POWERPOINT_FILES,
  ONE_KB,
  ONE_MB,
  ONE_GB,
  FIRST_PAGE,
  PAGE_PARAM_KEYWORD,
  NAME_MAX_LENGTH,
  NEW_WINDOW_PROPS,
  SMOOTH_SCROLL_CONFIGURATION,
  START_SCROLL_CONFIGURATION,
  EMPTY_ARRAY,
} from 'data/global';
import { Industry } from 'models/Industry';
import { NDA_STATUSES, NdaStatus } from 'models/Nda';
import {
  FILE_REORDER_VIEW_MODE,
  FileReorderViewMode,
  Role,
  User,
  USER_TYPE,
} from 'models/User';
import {
  DATA_ROOM_NODE_TYPE,
  DataRoomNode,
} from 'router/subrouters/DataRoom/models/DataRoomNode';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import {
  FILE_TYPES,
  FileTypes,
} from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/data/DataTypes';
import {
  SL_NDA_STATUS,
  ShareableLinkNdaStatus,
} from 'router/subrouters/DataRoom/models/DataRoomNDA';
import { Enterprise } from 'models/Enterprises';
import RwcImg from 'assets/images/rwc.webp';
import { EMAIL_PHONE_INPUT_TYPE } from 'components/EmailPhoneInputField/EmailPhoneInputField.component';
import { EmailOrPhoneForm } from 'models/AuthBody';
import credentialsService from 'services/credentialsService';
import {
  getContactNumber,
  getIsPhoneNumber,
  getIsValidPhone,
} from 'phoneNumberUtils';
import { REACTION_TYPE, ReactionType } from 'models/Reactions';
import { Q_AND_A_SIDES } from 'router/subrouters/DataRoom/models/QAndA';
import { Option } from 'models/Global';
import {
  NOTIFICATION_BY,
  NOTIFICATION_STATUS,
  NotificationBy,
  NotificationsStatus,
} from 'models/Notification';
import { PaginatedData } from 'models/PaginatedData';
import { DataRoomSubroute } from 'router/subrouters/DataRoom/models/DataRoom';
import {
  DataRoomLoginSource,
  DR_LOGIN_SOURCE_ID_PARAM,
  DR_LOGIN_SOURCE_PARAM,
} from 'models/DataRoomLoginSource';
import { DISCOVERING_FEATURE_VISIBILITY_DAYS } from 'data/discoveringFeature';
import { FilterOption } from 'models/Filters';

const NEW_LISTING_DAYS = 7;

export const min = 60,
  hour = min * 60,
  day = hour * 24,
  week = day * 7,
  month = day * 30,
  year = month * 12;

function shortPastTime(date: Date) {
  const currentDate = new Date();
  const delta = Math.abs((date.getTime() - currentDate.getTime()) / 1000);
  const getCount = (value: number) => parseInt((delta / value).toFixed(0));

  if (delta > year) return `${getCount(year)}y`;
  if (delta > week) return `${getCount(week)}w`;
  if (delta > day) return `${getCount(day)}d`;
  if (delta > hour) return `${getCount(hour)}h`;
  if (delta > min) return `${getCount(min)}m`;
  return `1m`;
}

const millisecondsPassed = (date: Date) => {
  const currentDate = new Date();
  return currentDate.getTime() - date.getTime();
};

export const FUTURE_KEYWORD = '_future';
export const PAST_KEYWORD = '_ago';

const getDateVariables = (date: Date) => {
  const delReal = (new Date(date).getTime() - new Date().getTime()) / 1000;
  const isFuture = delReal > 0;

  const seconds = Math.abs(delReal);
  const getCount = (value: number, truncate = false) =>
    truncate
      ? Math.trunc(seconds / value)
      : parseInt((seconds / value).toFixed(0));

  return {
    min,
    hour,
    day,
    week,
    month,
    year,
    seconds,
    isFuture,
    getCount,
  };
};

const humanizeSeconds = (seconds: number) => {
  const getCount = (value: number) => parseInt((seconds / value).toFixed(0));

  let fuzzy: PastTime;

  if (seconds > year)
    fuzzy = {
      type: `year`,
      count: getCount(year),
    };
  else if (seconds > month)
    fuzzy = {
      type: `month`,
      count: getCount(month),
    };
  else if (seconds > week)
    fuzzy = {
      type: `week`,
      count: getCount(week),
    };
  else if (seconds > day * 2)
    fuzzy = {
      type: `day`,
      count: getCount(day),
    };
  else if (seconds > day) fuzzy = { type: `yesterday` };
  else if (seconds > hour)
    fuzzy = {
      type: `hour`,
      count: getCount(hour),
    };
  else if (seconds > min)
    fuzzy = {
      type: `minute`,
      count: getCount(min),
    };
  else if (seconds > 30)
    fuzzy = {
      type: `second`,
      count: parseInt(seconds.toFixed(0)),
    };
  else fuzzy = { type: 'just_now' };

  return fuzzy;
};

function calculatePastTime(date: Date): PastTime {
  const currentDate = new Date();
  const delta = Math.abs((date.getTime() - currentDate.getTime()) / 1000);

  const future = new Date() < date;

  const humnizedObj = humanizeSeconds(delta);
  return {
    type: `${humnizedObj.type}${future ? FUTURE_KEYWORD : PAST_KEYWORD}`,
    count: humnizedObj.count,
  } as unknown as PastTime;
}

function humanize(date: string | Date): PastTime {
  let targetDate: Date;

  if (typeof date === 'string') targetDate = new Date(date);
  else targetDate = date;

  return calculatePastTime(targetDate);
}

function checkForZero(number: number) {
  return number < 10 ? `0${number}` : `${number}`;
}

export function toDateFormat(dateStr: string) {
  const date = new Date(dateStr);
  return `${checkForZero(date.getDate())} ${t(
    MONTHS[date.getMonth() + 1],
  )} ${date.getFullYear()}`;
}

function renderIcon(item: React.ReactNode | string) {
  if (typeof item === 'string') return item;

  const IconToRender = item as React.FC;
  return <IconToRender />;
}

function getDotedNumber(value: number | string = '') {
  if (value === 0) return '0';

  if (!value || isNaN(+value)) {
    return '';
  }

  const finalValue = value.toString();

  return finalValue
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    .split('.')
    .map((str, ind) => (ind > 0 ? str.replace(/,/g, '') : str))
    .join('.');
}

function getIntegerFromDottedNumber(value: string) {
  if (!value) return 0;
  return parseFloat(value.replace(/,/g, ''));
}

export const MONTHS = {
  1: 'jan',
  2: 'feb',
  3: 'mar',
  4: 'apr',
  5: 'may',
  6: 'jun',
  7: 'jul',
  8: 'aug',
  9: 'sep',
  10: 'oct',
  11: 'nov',
  12: 'dec',
} as Record<number, string>;

function getShortenedString(string: string, numberOfChar = 45) {
  if (string?.length > numberOfChar) {
    return `${string.slice(0, numberOfChar)}...`;
  }
  return string;
}

export const LESS_50_K = '< $50K';
const LESS_25_K = '< $25K';

export const mapValueToEbitdaFormat = (value: any) => {
  const price = +value;
  if (price < 12500) return LESS_25_K;
  if (price < 975000) return `$${Math.round(price / 25000) * 25}K`;
  return `$${Math.round(price / 100000) / 10}M`;
};

export const mapValueToMoneyFormat = (
  value: number | string,
  withZero = false,
) => {
  const price = +value;
  if (price === 0 && withZero) return `$${0}`;
  if (price < 25000) return LESS_50_K;
  if (price < 975000) return `$${Math.round(price / 50000) * 50}K`;
  return `$${Math.round(price / 100000) / 10}M`;
};

export const formatAskingPrice = (value: any) => {
  const price = +value;
  if (price < 25000) return LESS_50_K;
  if (price < 975000) return `$${Math.round(price / 50000) * 50}K`;
  if (price < 1000000) return `$1M`;
  return `$${Math.round(price / 10000) / 100}M`;
};

export const getAskingPrice = (
  listing: Partial<Listing>,
  showEoiPrice = false,
) => {
  if (!listing) return null;

  const { askingPriceSelect, amount, amountFrom, amountTo } = listing;

  if (askingPriceSelect === ASKING_PRICE_SELECT.FIXED_PRICE) {
    if (amount) return formatAskingPrice(amount);
    return null;
  }

  if (
    askingPriceSelect === ASKING_PRICE_SELECT.EXPRESSION_OF_INTEREST &&
    !showEoiPrice
  )
    return t('eoi');

  if (!!(amountFrom || amountFrom === 0) && !!amountTo) {
    const formatAmountFrom = formatAskingPrice(amountFrom);
    const formatAmountTo = formatAskingPrice(amountTo);

    if (formatAmountFrom === formatAmountTo) return formatAmountFrom;

    return `${formatAmountFrom} - ${formatAmountTo}`;
  }

  return null;
};

export const mapToMoneyFormat = (key: string, value: any) =>
  MONEY_PROPERTIES.some((el) => el === key)
    ? mapValueToMoneyFormat(value)
    : value;

export const convertMoneyToNumber = (string: string) =>
  +string.replace(/[,.]/g, '');

const MONEY_PROPERTIES = [
  'revenue',
  'ebitda',
  'averageStockValue',
  'askingPricePriceRange',
  'askingPrice',
  'amount',
  'amountFrom',
  'amountTo',
];

const changePropertyValueWith = (
  object: any,
  valueToChangeWith: any,
  valueToChange?: any,
): any => {
  if (!object) return object === valueToChange ? valueToChangeWith : object;
  if (typeof object !== 'object' || Array.isArray(object)) return object;

  const updatedValue = Object.entries(object).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]:
        value === valueToChange
          ? valueToChangeWith
          : changePropertyValueWith(value, valueToChangeWith, valueToChange),
    }),
    {},
  );

  return updatedValue;
};

const multiSelectValueToStringArray = (
  objectArray: Array<Record<string, any> & { value: string }>,
) =>
  objectArray.reduce(
    (acc, object) =>
      typeof object === 'object' && !Array.isArray(object) && !isNull(object)
        ? [...acc, object.value]
        : [...acc, object],
    [],
  );

const mapArrayToParams = (value: Array<string>) =>
  value ? safeStringify(value) || '' : '';

const mapParamsToArray = (value: string): string[] => {
  if (!value || typeof value !== 'string') return [];

  const parsed = safeParse(value);

  return Array.isArray(parsed) ? parsed : [];
};

const stringifyFilterValues = (val: string) => {
  const asArray = mapParamsToArray(val);
  return asArray.reduce(
    (acc, el, index) =>
      (acc += `${el}${index < asArray.length - 1 ? ', ' : ''}`),
    '',
  );
};

function isObject(object: any) {
  return object != null && typeof object === 'object';
}

function createSlug(str: string) {
  let text = normalizeText(str.toString());

  const sets = [
    { to: 'a', from: 'ÀÁÂÃÄÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶ' },
    { to: 'c', from: 'ÇĆĈČ' },
    { to: 'd', from: 'ÐĎĐÞ' },
    { to: 'e', from: 'ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ' },
    { to: 'g', from: 'ĜĞĢǴ' },
    { to: 'h', from: 'ĤḦ' },
    { to: 'i', from: 'ÌÍÎÏĨĪĮİỈỊ' },
    { to: 'j', from: 'Ĵ' },
    { to: 'ij', from: 'Ĳ' },
    { to: 'k', from: 'Ķ' },
    { to: 'l', from: 'ĹĻĽŁ' },
    { to: 'm', from: 'Ḿ' },
    { to: 'n', from: 'ÑŃŅŇ' },
    { to: 'o', from: 'ÒÓÔÕÖØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ' },
    { to: 'oe', from: 'Œ' },
    { to: 'p', from: 'ṕ' },
    { to: 'r', from: 'ŔŖŘ' },
    { to: 's', from: 'ßŚŜŞŠ' },
    { to: 't', from: 'ŢŤ' },
    { to: 'u', from: 'ÙÚÛÜŨŪŬŮŰŲỤỦỨỪỬỮỰƯ' },
    { to: 'w', from: 'ẂŴẀẄ' },
    { to: 'x', from: 'ẍ' },
    { to: 'y', from: 'ÝŶŸỲỴỶỸ' },
    { to: 'z', from: 'ŹŻŽ' },
    { to: '-', from: "·/_,:;'" },
  ];

  sets.forEach((set) => {
    text = text.replace(new RegExp(`[${set.from}]`, 'gi'), set.to);
  });

  const slug = encodeURIComponent(
    text
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/[^\w-]+/g, '') // Remove all non-word chars
      .replace(/--+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, ''), // Trim - from end of text
  );

  return slug.length > 0 ? slug : 'no-slug';
}

function composeBusinessUrl(
  industry: Industry,
  listingSlug: string,
  listingType: ListingType,
) {
  if (!listingSlug || !industry) return '';

  return `${
    checkIsUrgent(listingType) ? URGENT_ROUTE : BUSINESS_ROUTE
  }/${createSlug(industry)}/${listingSlug}`;
}

function appendUrlParams(url: string, params: Record<string, string>) {
  const [baseUrl, queryString] = url.split('?');

  const searchParams = new URLSearchParams(queryString);

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value);
    }
  });

  return `${baseUrl}?${searchParams.toString()}`;
}

export function sentryError(error: any) {
  if (env.SENTRY_DSN_URL) {
    Sentry.captureException(error);
  }
}

const areParamsEqual = (params: Params, prevparams: Params) =>
  (prevparams && Object.values(prevparams).length) ===
    (params && Object.values(params).length) &&
  Object.entries(params).every(
    ([key, value]) =>
      prevparams?.[key as any] && prevparams[key as any] === value,
  );

// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop(): any {}

function isSSGBot() {
  return (
    typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnapBot'
  );
}

function isSSGHuman() {
  return (
    typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnapHuman'
  );
}

function isSSG() {
  return isSSGBot() || isSSGHuman();
}

function replaceElementInArray<T>(
  oldArray: T[],
  oldValue: T,
  newValue: T | T[],
) {
  return oldArray.reduce((acc, item) => {
    if (item === oldValue) {
      return [...acc, ...(Array.isArray(newValue) ? newValue : [newValue])];
    } else {
      return [...acc, item];
    }
  }, []);
}

function replaceElementInStringArray(
  oldArray: string,
  oldValue: string,
  newValue: string | string[],
) {
  return safeStringify(
    replaceElementInArray<string>(safeParse(oldArray), oldValue, newValue),
  );
}

function downloadUrl(props: Partial<HTMLAnchorElement>) {
  const { target, href, download } = props;

  const a = document.createElement('a');

  a.href = href;

  if (target) a.target = target;

  // without it the browser falls back to the server's Content-Disposition, which a
  // preview signed url serves as `inline` — i.e. it navigates instead of downloading.
  if (download) a.download = download;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export const downloadUrlInNewWindow = (href: string, type: string) =>
  downloadUrl({
    href,
    type,
    ...NEW_WINDOW_PROPS,
  });

export function downloadBlob(blob: Blob, fileName: string) {
  const href = URL.createObjectURL(blob);

  downloadUrl({ href, download: fileName });

  // revoking in the same tick cancels the download in some browsers, so let the click
  // be handled first
  setTimeout(() => URL.revokeObjectURL(href));
}

export function getArrayOfEmails(emails: string[], emailsUnsubmited: string) {
  return isEmail(emailsUnsubmited)
    ? Array.from(new Set([...emails, emailsUnsubmited]))
    : emails;
}

export function capitalizeName(name: string) {
  if (!(name || '').trim()) return '';

  const parts: string[] = [];

  name
    .trim()
    .split(' ')
    .filter((part) => part)
    .forEach((part) =>
      parts.push(part[0].toUpperCase() + part.substring(1).toLowerCase()),
    );

  return parts.join(' ');
}

export function getMemberInitials(name = '', email = '') {
  const safeName = name?.trim();

  if (!safeName) return '';

  if (safeName) {
    const parts = safeName.split(' ').filter(Boolean);

    if (isSingleElementArray(parts)) return parts[0].slice(0, 2);

    return `${parts[0][0]}${parts[parts.length - 1][0]}`;
  }

  const safeEmail = email?.trim();

  if (!safeEmail) return '';

  const localPart = safeEmail.split('@')[0] || '';

  return localPart.slice(0, 2);
}

export const getDateInUserTimezone = (userTimeISOString: string, hours = 0) => {
  const dateObject = new Date(userTimeISOString);
  dateObject.setHours(hours);

  return dateObject.toISOString();
};

export const getTimeFromUserTimezone = (isoString: string) => {
  const date = new Date(isoString);
  const hours = date.getHours();

  return { hours };
};

const timeSpentInSecondsHumanize = (seconds: number, showInSeconds = false) => {
  const getCount = (value: number) => parseInt((seconds / value).toFixed(0));

  if (seconds > hour)
    return {
      type: 'hour',
      count: getCount(hour),
    };

  if (seconds >= min)
    return {
      type: 'minute',
      count: getCount(min),
    };

  if (showInSeconds) return { type: 'second', count: seconds };

  return {
    type: 'minute',
    count: 0,
  };
};

// size in BYTES;
const humanizeFileSize = (size: number) => {
  if (!size) return '';

  if (size < ONE_KB) return '<1 KB';

  if (size < ONE_MB) return `${(size / ONE_KB).toFixed(0)} KB`;

  if (size < ONE_GB) return `${parseFloat((size / ONE_MB).toFixed(1))} MB`;

  return `${parseFloat((size / ONE_GB).toFixed(1))} GB`;
};

export const stringifyMembersCount = (
  count: number,
): [string, Record<string, any>] => {
  if (!count || count === 0) return ['onlyYou', {}];

  return ['membersCount', { count }];
};

function generateNumberOfYears(numberOfYears: number, isFuture?: boolean) {
  const currentYear = new Date().getFullYear().toString().slice(-2);

  return [...Array(numberOfYears).keys()].map(
    (i) => +currentYear - (isFuture ? -(i + 1) : i),
  );
}

export const getHrefFromUrl = (url: string) => {
  if (!isUrl(url)) return '';

  return url.startsWith('http') ? url : `//${url}`;
};

const getLinkedinUrlAndVanityName = (linkedinAccount: string) => {
  if (!linkedinAccount) return { linkedinUrl: null, vanityName: null };

  const linkedinUrl = getHrefFromUrl(linkedinAccount);

  const index = linkedinAccount?.indexOf('/in');
  const vanityName = linkedinAccount.substring(index);

  return { linkedinUrl, vanityName };
};

export const removeUndefinedValuesFromArray = (array: any[]) =>
  array?.filter((el) => !isUndefined(el));

export const removeUndefinedValuesFromArrayFields = (
  arrayFields: Record<string, Array<string | undefined>>,
) =>
  Object.entries(arrayFields).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: removeUndefinedValuesFromArray(value),
    }),
    {},
  );

export const transformCurrencyPropertiesToNumber = (
  values: Record<string, any>,
  props: string[],
) =>
  Object.entries(values).reduce((acc, [key, value]) => {
    return props.some((el) => el === key)
      ? { ...acc, [key]: getIntegerFromDottedNumber(value as string) }
      : acc;
  }, {});

export const transformPriceNumberToCurrencyString = (price: number) =>
  `$${getDotedNumber(price || 0)}`;

/**
 * Detects whether input value is email, phone, or unknown
 */
export const getEmailPhoneInputType = (value: string) => {
  if (!value || !value.trim()) return EMAIL_PHONE_INPUT_TYPE.UNKNOWN;

  const trimmedValue = value.trim();

  // Check for email first (contains @ symbol and passes email validation)
  if (trimmedValue.includes('@') && isEmail(trimmedValue)) {
    return EMAIL_PHONE_INPUT_TYPE.EMAIL;
  }

  // Check for phone number
  if (getIsPhoneNumber(trimmedValue)) {
    return EMAIL_PHONE_INPUT_TYPE.PHONE;
  }

  return EMAIL_PHONE_INPUT_TYPE.UNKNOWN;
};

export const getCreateListingRoute = (
  dealId: string,
  listingId: string,
  listingType: ListingType,
  isEdit = false,
) =>
  `${LCW_BASE_ROUTE}/${dealId}${isEdit ? '/edit' : ''}${
    LISTING_TYPE_ROUTES[listingType].route
  }/${listingId}`;

export const getCreateDealRoute = (
  dealRoomId: string,
  isEdit = false,
  subroute?: 'market-listing',
) =>
  `${isEdit ? EDIT_DEAL_ROUTE : CREATE_DEAL_ROUTE}/${dealRoomId}${addSubroute(
    subroute,
  )}`;

export const getPrivateListingRoute = (listingId: string) =>
  `${PRIVATE_LISTING_ROUTE}/${listingId}`;

export const areArraysEqual = (first: any[], second: any[]) =>
  first.length === second.length &&
  first.every((f) => second.some((s) => isEqual(f, s)));

export const isVideo = (type: string) => (type || '').includes('video');

export const getFileNameWithoutExtension = (fileName = '') =>
  fileName.replace(/\.[^./\\]+$/, '');

export const getFileExtension = (fileName = '') =>
  (fileName.match(/\.([^./\\]+)$/)?.[1] || '').toLowerCase();

export const isDwg = (type: string) => {
  const normalizedType = (type || '').toLowerCase();
  return (
    normalizedType === FILE_TYPES.DWG ||
    normalizedType.includes('dwg') ||
    normalizedType.includes('autocad')
  );
};

export const isImage = (type: string) =>
  (type || '').includes('image') && !isDwg(type);

export const isPdf = (type: string) => type === FILE_TYPES.PDF;

export const isWord = (type: string) => WORD_FILES.includes(type as FileTypes);

export const isEml = (type: string) => type === FILE_TYPES.EML;

export const isSheet = (type: string) =>
  SHEET_FILE_TYPES.includes(type as FileTypes);

export const isPowerpoint = (type: string) =>
  POWERPOINT_FILES.includes(type as FileTypes);

export const isOfficeFileType = (type: string) =>
  isWord(type) || isSheet(type) || isPowerpoint(type);

export const getMaxElementInArray = (values: number[]) => Math.max(...values);

export const getPlatform = (): DesktopAppPlatform | null => {
  const { userAgent } = navigator;

  if (!userAgent) return null;

  if (/Mac/.test(userAgent)) return DESKTOP_APP_PLATFORM.MACOS;

  if (/Linux/.test(userAgent)) return DESKTOP_APP_PLATFORM.LINUX;

  if (/Win/.test(userAgent)) return DESKTOP_APP_PLATFORM.WINDOWS;

  return null;
};

export const scrollTo = (
  scrollToOptions: ScrollToOptions = START_SCROLL_CONFIGURATION,
) =>
  setTimeout(
    () =>
      window.scrollTo({
        ...SMOOTH_SCROLL_CONFIGURATION,
        ...scrollToOptions,
      }),
    0,
  );

export const scrollToTop = () => scrollTo();

export const scrollToElement = (selector: string) => {
  const element = document.querySelector(selector);

  if (!element) return;

  element.scrollIntoView({ ...SMOOTH_SCROLL_CONFIGURATION, block: 'center' });
};

export const scrollElementIntoHorizontalCenter = (
  container: HTMLElement,
  element: HTMLElement,
  behavior: ScrollBehavior = 'smooth',
) => {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  const { scrollWidth, clientWidth, scrollLeft } = container || {};
  const { left, width } = elementRect || {};

  const maxScrollLeft = scrollWidth - clientWidth;
  const targetScrollLeft =
    scrollLeft + (left - containerRect.left) - (clientWidth - width) / 2;

  container.scrollTo({
    behavior,
    left: Math.max(0, Math.min(targetScrollLeft, maxScrollLeft)),
  });
};

export const scrollToErrors = () =>
  setTimeout(() => scrollToElement('[class*="error"]'), 0);

export const isNewListing = (
  createdAt: Date | string,
  days = NEW_LISTING_DAYS,
) => {
  if (!createdAt) return false;
  return isDateFromLastDays(createdAt, days);
};

export const checkIsArchived = (dealStage: DealStage) =>
  dealStage === DEAL_STAGE.ARCHIVED;

export const checkIsSold = (dealStage: DealStage) =>
  dealStage === DEAL_STAGE.SOLD;

export const checkIsArchivedOrSold = (dealStage: DealStage) =>
  checkIsArchived(dealStage) || checkIsSold(dealStage);

export const checkIsNoListing = (listingStage: ListingStage) =>
  listingStage === LISTING_STAGE.NO_LISTING;

export const checkIsDraft = (listingStage: ListingStage) =>
  listingStage === LISTING_STAGE.DRAFT;

export const checkIsPublic = (listingStage: ListingStage) =>
  listingStage === LISTING_STAGE.PUBLIC;

export const checkIsPrivate = (listingStage: ListingStage) =>
  listingStage === LISTING_STAGE.PRIVATE;

export const checkIsPaused = (listingStage: ListingStage) =>
  listingStage === LISTING_STAGE.PAUSED;

export const checkIsEnded = (listingStage: ListingStage) =>
  listingStage === LISTING_STAGE.ENDED;

export const checkHasListing = (listingStage: ListingStage) =>
  checkIsPublic(listingStage) || checkIsPrivate(listingStage);

export const checkIsPrivateDrafted = (
  listingStage: ListingStage,
  wantToBePrivate: boolean,
) => checkIsDraft(listingStage) && wantToBePrivate;

export const checkHasListingIncludingPrivateDrafted = (
  listingStage: ListingStage,
  wantToBePrivate: boolean,
) =>
  checkHasListing(listingStage) ||
  checkIsPrivateDrafted(listingStage, wantToBePrivate);

export const checkCanProlongListing = (listingStage: ListingStage) =>
  checkIsPublic(listingStage) || checkIsEnded(listingStage);

export const getListingStage = (
  listingStage: ListingStage,
  wantToBePrivate: boolean,
) =>
  checkIsPrivateDrafted(listingStage, wantToBePrivate)
    ? LISTING_STAGE.PRIVATE
    : listingStage;

export const checkIsUrgent = (listingType: ListingType) =>
  listingType === LISTING_URGENT;

export const checkIsNoPackage = (listingPackage: ListingPackage) =>
  listingPackage === LISTING_PACKAGE.NO_PACKAGE;

export const checkIsTierInitiallyStandard = (listingPackage: ListingPackage) =>
  listingPackage === LISTING_PACKAGE.BRONZE || checkIsNoPackage(listingPackage);

export const checkIsTierInitiallyPremium = (listingPackage: ListingPackage) =>
  listingPackage === LISTING_PACKAGE.SILVER ||
  listingPackage === LISTING_PACKAGE.BASIC;

export const checkIsTierInitiallyPlatinum = (listingPackage: ListingPackage) =>
  listingPackage === LISTING_PACKAGE.GOLD ||
  listingPackage === LISTING_PACKAGE.COMPREHENSIVE;

export const checkIsTierStandard = (listingTier: ListingTier) =>
  listingTier === LISTING_TIERS.STANDARD;

export const checkIsTierPremium = (listingTier: ListingTier) =>
  listingTier === LISTING_TIERS.PREMIUM;

export const checkIsTierPlatinum = (listingTier: ListingTier) =>
  listingTier === LISTING_TIERS.PLATINUM;

export const checkCanPaused = (listingStage: ListingStage) =>
  !checkIsPaused(listingStage) && checkHasListing(listingStage);

export const checkCanUnpaused = (
  dealStage: DealStage,
  listingStage: ListingStage,
) =>
  !checkIsSold(dealStage) &&
  !checkIsArchived(dealStage) &&
  checkIsPaused(listingStage);

export const checkIsPublished = (listingStage: ListingStage) =>
  checkHasListing(listingStage) ||
  checkIsPaused(listingStage) ||
  checkIsEnded(listingStage);

export const checkIsPublishedOrPrivateDrafted = (
  listingStage: ListingStage,
  wantToBePrivate: boolean,
) =>
  checkIsPublished(listingStage) ||
  checkIsPrivateDrafted(listingStage, wantToBePrivate);

export const getFeeShare = (total: number, shareInPercentage: string) =>
  getDotedNumber((total * parseInt(shareInPercentage)) / 100);

export const getFeeSharePercentage = (total: number, share: string) =>
  share && total
    ? ((getIntegerFromDottedNumber(share) / total) * 100).toString()
    : '';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const isEmail = (email: string) => EMAIL_REGEX.test(email || '');

const URL_REGEX =
  /^(https?:\/\/)?([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,})(\/[^\s]*)?(\?[^\s]*)?$/;

export const isUrl = (url: string) => URL_REGEX.test(url);

export const checkIsValidEmail = (value: string) => !!value && isEmail(value);

const INTERNATIONAL_NAME_REGEX =
  /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$/;

export const isName = (value: string) => INTERNATIONAL_NAME_REGEX.test(value);

export const checkIsValidName = (value = '') =>
  value?.length <= NAME_MAX_LENGTH && isName(value);

export const LINKEDIN_URI_REGEX = new RegExp(
  '^(https?://)?(www.)?linkedin.com/in/([^/]+)/?$',
);

export const isLinkedinUrl = (value = '') => LINKEDIN_URI_REGEX.test(value);

export const getPortfolioRoute = (
  isBuyer: boolean,
  dealRoomId: string,
  listingEnquiryId: string,
) =>
  isBuyer
    ? getBuyingPortfolio(listingEnquiryId)
    : getSellingPortfolio(dealRoomId);

const addSubroute = (subroute: string) => (subroute ? `/${subroute}` : '');

export const getBuyingPortfolio = (
  listingEnquiryId: string,
  subroute?: 'messaging' | 'listing-information',
) => `${BUYING_PORTFOLIO_ROUTE}/${listingEnquiryId}${addSubroute(subroute)}`;

export const getSellingPortfolio = (
  dealRoomId: string,
  subroute?:
    | 'messaging'
    | 'leads'
    | 'listing-information'
    | 'gallery'
    | 'deal'
    | 'settings',
) => `${SELLING_PORTFOLIO_ROUTE}/${dealRoomId}${addSubroute(subroute)}`;

// Tags a finished Data Room path with the source the member is entering from.
// Use this directly when the caller appends further path segments after
// `getDataRoomRoute`, so the query string stays at the end of the URL.
export const withDataRoomSource = (
  path: string,
  source?: DataRoomLoginSource,
  sourceId?: string,
) => {
  if (!source) return path;

  const params = new URLSearchParams({ [DR_LOGIN_SOURCE_PARAM]: source });

  if (sourceId) params.set(DR_LOGIN_SOURCE_ID_PARAM, sourceId);

  return `${path}${path.includes('?') ? '&' : '?'}${params.toString()}`;
};

export const getDataRoomRoute = (
  dataRoomId: string,
  subroute?: DataRoomSubroute,
  source?: DataRoomLoginSource,
  sourceId?: string,
) =>
  withDataRoomSource(
    `${DATA_ROOM_ROUTE}/${dataRoomId}${addSubroute(subroute)}`,
    source,
    sourceId,
  );

export const getEnterpriseRoute = (
  isRwc: boolean,
  isRwcDemo: boolean,
  enterpriseId?: string,
  subroute?: 'analytics' | 'team' | 'deals' | 'configuration' | 'branding',
) =>
  isRwc
    ? isRwcDemo
      ? `${RWC_ROUTE.toLowerCase()}/${enterpriseId}`
      : RWC_ROUTE
    : `${ENTERPRISE_ROUTE}/${enterpriseId}${addSubroute(subroute)}`;

export const getShareableLinkRoute = (shareableLinkId: string) =>
  `${SHAREABLE_LINKS_ROUTE}/${shareableLinkId}`;

export const getRFILinkRoute = (externalKey: string) =>
  `${RFI_LINK}/${externalKey}`;

export const addPercentageSuffix = (value: string | number) => {
  if (!value) return '';

  const retValue = value.toString();

  return retValue.includes(PERCENTRAGE)
    ? retValue
    : `${retValue}${PERCENTRAGE}`;
};

export const extractValueFromPercentage = (
  value: string | number,
  oldValue: string | number,
) => {
  if (!value) return '';

  const retVal = value.toString();
  const retOldVal = oldValue ? oldValue.toString() : '';

  return retVal.includes(PERCENTRAGE)
    ? retVal.replaceAll(PERCENTRAGE, '').trim()
    : retOldVal === retVal
    ? retVal.slice(0, -1)
    : retVal;
};

export const addDollarPreffix = (value: string) => {
  if (!value || value === DOLLAR) return '';

  return `${DOLLAR}${getDotedNumber(removeCommaAndDolar(value))}`;
};

export const extractValueFromMoneyString = (value: string) => {
  if (!value) return '';

  return getDotedNumber(removeCommaAndDolar(value));
};

const removeCommaAndDolar = (value: string) =>
  value.replaceAll(',', '').replaceAll(DOLLAR, '');

export const checkIsKeyInObject = (key: string, values: Record<string, any>) =>
  key in values;

export function updateKeyInObject<T>(
  values: T,
  oldKey: keyof T,
  newKey: keyof T,
) {
  return Object.entries(values).reduce(
    (acc, [key, value]) => ({ ...acc, [key === oldKey ? newKey : key]: value }),
    {} as T,
  );
}

export const getUrlSearchParams = () =>
  new URLSearchParams(window.location.search);

export const formatPercentage = (value: string | number) =>
  `${value}${PERCENTRAGE}`;

export const getIsListingOwner = (
  currentUserId: string,
  listingOwnerId: string,
  isCoOwner: boolean,
) => currentUserId === listingOwnerId || isCoOwner;

export const sortColleagues = (colleagues: Colleague[]) => {
  const owner = colleagues.find(({ isOwner }) => isOwner);

  return owner
    ? [owner, ...colleagues.filter(({ isOwner }) => !isOwner)]
    : colleagues;
};

export const isKeyPressed = (e: KeyEvent, key: string) => e.key === key;

export const isEscapePressed = (e: KeyEvent) => isKeyPressed(e, ESCAPE_KEY);

export const isEnterPressed = (e: KeyEvent) => isKeyPressed(e, ENTER_KEY);

// SPACE_KEY is a `code` value; React 16 types don't expose `code` on synthetic events
export const isSpacePressed = (e: KeyEvent) =>
  ('nativeEvent' in e ? e.nativeEvent : e).code === SPACE_KEY;

export const isTabPressed = (e: KeyEvent) => isKeyPressed(e, TAB_KEY);

export const isArrowDownPressed = (e: KeyEvent) =>
  isKeyPressed(e, ARROW_DOWN_KEY);

export const isArrowUpPressed = (e: KeyEvent) => isKeyPressed(e, ARROW_UP_KEY);

export const isArrowLeftPressed = (e: KeyEvent) =>
  isKeyPressed(e, ARROW_LEFT_KEY);

export const isArrowRightPressed = (e: KeyEvent) =>
  isKeyPressed(e, ARROW_RIGHT_KEY);

export const shouldShowFrachisorFee = (isEnterpriseMember: boolean) =>
  !isEnterpriseMember;

export const getShouldHasRevenueShares = (
  projectedSalePrice: string | number,
  successFeePercentage: string | number,
) =>
  getIntegerFromDottedNumber(projectedSalePrice?.toString()) > 0 &&
  getIntegerFromDottedNumber(successFeePercentage?.toString()) > 0;

export const isNdaSigned = (signingStatus: NdaStatus) =>
  signingStatus === NDA_STATUSES.SIGNED;

export const isNdaSent = (signingStatus: NdaStatus) =>
  signingStatus === NDA_STATUSES.SENT;

export const isNdaNotSent = (signingStatus: NdaStatus) =>
  signingStatus === NDA_STATUSES.NOT_SENT;

export const isNdaRejected = (signingStatus: NdaStatus) =>
  signingStatus === NDA_STATUSES.REJECTED;

export const isNdaCounterSignerPending = (signingStatus: NdaStatus) =>
  signingStatus === NDA_STATUSES.COUNTER_SIGNATURE_PENDING;

export const checkCanSendNda = (signingStatus: NdaStatus) =>
  isNdaNotSent(signingStatus) || isNdaSent(signingStatus);

export const removeLINK = (unit: string) => unit.replaceAll('LINK', '').trim();

export const checkIsUserSuperAdmin = (role: Role) =>
  role === USER_TYPE.SUPER_ADMIN || role === USER_TYPE.ADMIN;

export const checkIsUltraAdmin = (role: Role) => role === USER_TYPE.SUPER_ADMIN;

export const isDefaultSellingPortfolio = (
  isEnterpriseMember: boolean,
  hasSellingDeals: boolean,
) => isEnterpriseMember || hasSellingDeals;

export const getEditUserRoute = (userId: string) =>
  `${ADMIN_DASHBOARD_EDIT_PROFILE}/${userId}`;

export const isOpenDrawerOrModal = () =>
  !!document.getElementsByClassName('ncoded-modal').length ||
  !!document.getElementsByClassName('open').length;

export const getAcceptedListingContacts = (contacts: ListingContactType[]) =>
  (contacts || []).filter(({ name }) => !!name);

export const toggleValueInArray = (array: any[], value: any) =>
  array.some((el) => el === value)
    ? array.filter((el) => el !== value)
    : [...array, value];

export const doesObjectHasTrutfullyValue = (object: Record<string, any>) =>
  Object.values(object || {}).some(Boolean);

export const safeStringify = (value?: any) => {
  try {
    return JSON.stringify(value);
  } catch (err) {
    return null;
  }
};

export const safeParse = (value?: any) => {
  try {
    return JSON.parse(value);
  } catch (e) {
    return undefined;
  }
};

export const checkIsTextValidJSON = (text: string) => !!safeParse(text);

export const isUndefined = (value: any) => value === undefined;

export const isNull = (value: any) => value === null;

export const doesValueExist = (value: any) =>
  !isUndefined(value) && !isNull(value);

export const replaceNullishValues = (
  object: any,
  replacementValue: any,
): any => {
  if (object == null || object === undefined) return replacementValue;

  if (Array.isArray(object))
    return object.map((value) => replaceNullishValues(value, replacementValue));

  if (typeof object !== 'object') return object;

  return Object.entries(object).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: replaceNullishValues(value, replacementValue),
    }),
    {},
  );
};

export const getSortValue = (sort = '', isDesc = false) =>
  isDesc ? `-${sort}` : sort;

export const getIsMultiSort = (sort: string) =>
  sort.includes(MULTI_SORT_SEPARATOR);

export const getIsDesc = (sort = '') => sort.startsWith('-');

export const getIsSortApplied = (sort = '', sortingValue = '') =>
  sort.includes(sortingValue);

export const getIsSortedDescBy = (sort = '', field = '') =>
  getIsMultiSort(sort) ? sort.includes(`-${field}`) : getIsDesc(sort);

export const getSortBy = (sort = '') =>
  getIsDesc(sort) ? sort.replace('-', '') : sort;

export const getSortComparator = (aVal: any, bVal: any, isDesc = false) => {
  if (aVal === bVal) return 0;

  if (isDesc) return aVal > bVal ? -1 : 1;

  return aVal > bVal ? 1 : -1;
};

/**
 * Builds a comparator for a multi-sort string (`'-status views'`) sorted on the
 * FE. Fields are applied in order and the first non-equal one wins, so later
 * entries act as tie-breakers. A single-entry string behaves like a plain sort.
 */
export const getMultiSortComparator =
  <T,>(sort = '', valueGetters: Record<string, (item: T) => string | number>) =>
  (a: T, b: T) => {
    for (const entry of sort.split(MULTI_SORT_SEPARATOR).filter(Boolean)) {
      const getValue = valueGetters[getSortBy(entry)];

      if (!getValue) continue;

      const aValue = getValue(a);
      const bValue = getValue(b);

      const comparison =
        typeof aValue === 'string' && typeof bValue === 'string'
          ? aValue.localeCompare(bValue)
          : getSortComparator(aValue, bValue);

      if (comparison) return getIsDesc(entry) ? -comparison : comparison;
    }

    return 0;
  };

const DATE_SORT_KEYS = ['modifiedAt', 'viewedDate'];

const NODE_SORT_VALUE_GETTERS: Record<string, (node: DataRoomNode) => number> =
  {
    size: (node) => getNodeSize(node),
    isStarred: ({ isStarred }) => (isStarred ? 1 : 0),
    childrenCount: (node) => getArrayLength(node.directChildrenIds),
  };

const sortNodes = (nodes: DataRoomNode[], sortBy: string, isDesc = false) =>
  nodes.sort((a, b) => {
    const isKeyDate = DATE_SORT_KEYS.includes(sortBy);
    const sortValueGetter = NODE_SORT_VALUE_GETTERS[sortBy];

    let aVal;
    let bVal;

    if (sortValueGetter) {
      aVal = sortValueGetter(a);
      bVal = sortValueGetter(b);
    } else {
      const sorter = sortBy as keyof DataRoomNode;

      aVal = isKeyDate ? new Date(a[sorter] as Date).getTime() : a[sorter];
      bVal = isKeyDate ? new Date(b[sorter] as Date).getTime() : b[sorter];
    }

    const comparator = getSortComparator(aVal, bVal, isDesc);
    return comparator * (isKeyDate ? -1 : 1);
  });

export const sortDataRoomNodes = (
  nodes: DataRoomNode[] = [],
  sort = 'sortName',
) => {
  const folders: DataRoomNode[] = [];
  const files: DataRoomNode[] = [];

  nodes.forEach((node) =>
    getIsNodeFile(node) ? files.push(node) : folders.push(node),
  );

  const isDesc = getIsDesc(sort);
  const sortBy = getSortBy(sort);

  const sortedFolder = sortNodes(folders, sortBy, isDesc);
  const sortedFiles = sortNodes(files, sortBy, isDesc);

  return [...sortedFolder, ...sortedFiles];
};

export const getShouldAnswerQuestionsOnEnquire = ({
  shouldAskQuestions,
  enabledQualificationQuestions,
  qualificationQuestions,
}: Partial<DealRoom>) =>
  shouldAskQuestions &&
  enabledQualificationQuestions &&
  !!qualificationQuestions?.length;

export const formatWithLimit = (value: number, limit = 9) =>
  value > limit ? `${limit}+` : value;

export const getIsNodeFile = (node: Partial<DataRoomNode>) =>
  !!node && !!node.document;

export const getIsNodeFolder = (node: Partial<DataRoomNode>) =>
  !!node && !node.document;

export const getNodeSize = (node: Partial<DataRoomNode>) =>
  getIsNodeFolder(node) ? node?.folderSize : node?.document?.size;

export const getFileCountInFolder = (node: Partial<DataRoomNode>) => {
  if (getIsNodeFile(node)) return 0;

  return node.childrenIds.length;
};

export const getIsFolderEmpty = (node: Partial<DataRoomNode>) =>
  getIsNodeFolder(node) && getFileCountInFolder(node) === 0;

export const getNodeMimeType = (node: Partial<DataRoomNode>) =>
  node?.document?.originalType || node?.document?.type;

export const getDataRoomNodeType = (node: DataRoomNode) =>
  getIsNodeFile(node) ? DATA_ROOM_NODE_TYPE.FILE : DATA_ROOM_NODE_TYPE.FOLDER;

export const getCanDownloadFolder = (node: DataRoomNode) =>
  !getIsFolderEmpty(node) && !!node.numberOfAccessableFilesForDownload;

export const getCanDownloadNode = (node: DataRoomNode) =>
  getIsNodeFile(node) ? node.canDownload : getCanDownloadFolder(node);

export const getIsRevisedVersion = (version: number) => version > 1;

export const getPercentage = (value: number, total: number) =>
  ((value / total) * 10000) / 100;

export const getPercentageWithTwoDecimals = (value: number, total: number) =>
  ~~getPercentage(value, total);

export const scrollToErrorsOnSubmit = (
  invalid: boolean,
  submit: () => void,
) => {
  submit();
  if (invalid) scrollToErrors();
};

export const displayDataRoomNodeSize = (nodeSize: number) =>
  nodeSize ? humanizeFileSize(nodeSize) : '--';

export const getDownloadName = () =>
  `resolve-download-${getFormattedDate(new Date(), DATE_FORMAT_FULL_DATETIME)}`;

export const getUserFullName = (firstName: string, lastName?: string) => {
  if (!firstName && !lastName) return '';

  if (!lastName) return capitalizeName(firstName);

  if (!firstName) return capitalizeName(lastName);

  return capitalizeName(`${firstName} ${lastName}`);
};

export const getUserFullNameOrEmail = (
  user: Partial<Pick<User, 'firstName' | 'lastName' | 'email'>>,
) =>
  getUserFullName(user?.firstName, user?.lastName) ||
  normalizeEmail(user?.email) ||
  '';

// users without a last name are shown as "Isaac (isaac.ruppert@gmail.com)"
export const getUserFullNameWithEmail = (
  firstName: string,
  lastName?: string,
  email?: string,
) => {
  const fullName = getUserFullName(firstName, lastName);
  const normalizedEmail = normalizeEmail(email);

  if (!fullName) return normalizedEmail;

  if (lastName || !normalizedEmail) return fullName;

  return `${fullName} (${normalizedEmail})`;
};

// env.URL = https://dev.resolve.ncoded.xyz
export const getResolveUrl = (path: string) => env.URL + path;

export const getFileIdsForShareableLinks = (
  nodes: DataRoomNode[],
  canShareBuySideNodes?: boolean,
) =>
  nodes.flatMap((node) => {
    if (getIsNodeFile(node))
      return !node.isBuyerFile || canShareBuySideNodes ? [node._id] : [];

    return getShareableChildIds(node, canShareBuySideNodes) || [];
  });

export const isShareableLinkNdaProcessing = (status: ShareableLinkNdaStatus) =>
  status === SL_NDA_STATUS.WAITING_CONNECTION;

export const getIsUserVerified = (
  hasVerifiedEmail: boolean,
  hasVerifiedContactNumber: boolean,
) => hasVerifiedEmail && hasVerifiedContactNumber;

export const getCheckedValuesInObject = (object: Record<string, any>) =>
  Object.entries(object || {})
    .filter(([_, value]) => !!value)
    .map(([key]) => key);

export const createRecordFromKeys = (array: string[], value: any) =>
  Object.fromEntries(array.map((el) => [el, value]));

export const getJoinedWordsWithComma = (words: string[] = []) => {
  if (!words || getIsArrayEmpty(words)) return '';

  const filteredWords = words.filter((word) => !!word);

  if (getIsArrayEmpty(filteredWords)) return '';

  if (isSingleElementArray(filteredWords)) return filteredWords[0];

  const lastWord = filteredWords.pop();

  return `${filteredWords.join(', ')} and ${lastWord}`;
};

export const isEnterpriseRWC = ({ isRWC }: Partial<Enterprise> = {}) => isRWC;

export const getDealImage = (imageSrc: string, isRWC: boolean) =>
  imageSrc || (isRWC ? RwcImg : null);

const isScrollable = (overflow: string) =>
  overflow === 'auto' || overflow === 'scroll';

export const getScrollParent = (
  element: HTMLElement | null,
  shouldIncludeXScroll = false,
): HTMLElement | null => {
  if (!element) return null;

  let parent = element.parentElement;

  while (parent) {
    const { overflowX, overflowY } = getComputedStyle(parent);

    if (
      isScrollable(overflowY) ||
      (shouldIncludeXScroll && isScrollable(overflowX))
    ) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return document.documentElement;
};

export const getIsOwner = (ownerId: string, userId: string) =>
  ownerId === userId;

export const isSuperAdminOrOwner = (
  isSuperAdmin: boolean,
  ownerId: string,
  currentUserId: string,
) => isSuperAdmin || getIsOwner(ownerId, currentUserId);

export const openMail = (mail: string, subject?: string) =>
  `mailto:${mail}${subject ? `?subject=${subject}` : ''}`;

export const getArrayLength = (array: any[] = []) => array.length;

export const isSingleElementArray = (array: any[] = []) =>
  getArrayLength(array) === 1;

export const getHasElementsInArray = (array: any[] = []) =>
  getArrayLength(array) > 0;

export const getIsArrayEmpty = (array: any[] = []) =>
  !getHasElementsInArray(array);

export const sortUsers = (
  users: Partial<Pick<User, 'firstName' | 'lastName'>>[],
) =>
  [...users].sort((a, b) =>
    getSortComparator(
      getUserFullName(a?.firstName, a?.lastName),
      getUserFullName(b?.firstName, b?.lastName),
    ),
  );

export const sortUsersByNameThenEmail = <
  T extends Partial<Pick<User, 'firstName' | 'lastName' | 'email'>>,
>(
  users: T[],
) =>
  [...users].sort((first, second) => {
    const firstFullName = getUserFullName(first.firstName, first.lastName);
    const secondFullName = getUserFullName(second.firstName, second.lastName);

    const firstHasName = !!firstFullName;
    const secondHasName = !!secondFullName;

    if (firstHasName !== secondHasName) return firstHasName ? -1 : 1;

    const firstValue = getUserFullNameOrEmail(first).toLowerCase();

    const secondValue = getUserFullNameOrEmail(second).toLowerCase();

    return getSortComparator(firstValue, secondValue);
  });

export const getCurrentDateInString = () => new Date().toString();

export const getCurrentDateInMiliSeconds = () => +new Date();

export const getElapsedTimeInSecs = (startedAt: number) =>
  Math.round((+new Date() - startedAt) / 1000);

export const addDaysToDate = (days: number, date?: Date) => {
  const result = date ? new Date(date) : new Date();

  result.setDate(result.getDate() + days);

  return result;
};

export const getEmailOrPhoneForLoginOrRegister = ({
  email,
  emailOrPhone,
}: EmailOrPhoneForm) => {
  const trimmedEmail = email?.trim();
  const trimmedEmailOrPhone = emailOrPhone?.trim();

  if (trimmedEmail) {
    return { email: trimmedEmail };
  }

  if (trimmedEmailOrPhone) {
    const detectedType = getEmailPhoneInputType(trimmedEmailOrPhone);

    if (detectedType === EMAIL_PHONE_INPUT_TYPE.PHONE) {
      return { contactNumber: trimmedEmailOrPhone.replaceAll(' ', '') };
    }
  }

  return { email: trimmedEmailOrPhone || '' };
};

export const includesIgnoreCase = (source: string, search: string) => {
  return source?.toLowerCase()?.includes(search?.toLowerCase());
};

export const getIsCurrentUser = (userId: string) =>
  credentialsService.currentUserId === userId;

export const getShareableChildIds = (
  node: Partial<DataRoomNode>,
  canShareBuySideNodes?: boolean,
) =>
  canShareBuySideNodes
    ? node.childrenIds
    : node.childrenIds?.filter((id) => !node.buySideFileIds?.includes(id));

export const getShowNodeHasBuyerFiles = (node: Partial<DataRoomNode>) =>
  getIsNodeFolder(node) && !!node.buySideFileIds?.length;

export const getCanShareFolder = (node: Partial<DataRoomNode>) =>
  !getIsFolderEmpty(node) && !!getShareableChildIds(node).length;

export const getCanShareFile = ({
  userId,
  isBuyerFile,
}: Partial<DataRoomNode>) => getIsCurrentUser(userId) || !isBuyerFile;

export const getCanShareNode = (node: Partial<DataRoomNode>) =>
  getIsNodeFolder(node) ? getCanShareFolder(node) : getCanShareFile(node);

export const renderFullUserInfo = (user: Partial<User>) => {
  const { firstName, lastName, phone, email } = user;

  const fullName = getUserFullName(firstName, lastName);

  const phoneNumber = getIsValidPhone(phone) ? getContactNumber(phone) : '';

  const contactInfo = [normalizeEmail(email), phoneNumber]
    .filter(Boolean)
    .join(', ');

  return contactInfo ? `${fullName} (${contactInfo})` : fullName;
};

export const isThumbsUpReaction = (reaction?: ReactionType | null) =>
  reaction === REACTION_TYPE.THUMBS_UP;

export const isThumbsDownReaction = (reaction?: ReactionType | null) =>
  reaction === REACTION_TYPE.THUMBS_DOWN;

export const isQuestionMarkReaction = (reaction?: ReactionType | null) =>
  reaction === REACTION_TYPE.QUESTION_MARK;

export const extractNodeWatermarkSettings = ({
  fileSecurity,
  expirationDays,
  blurProtection,
  watermarkDiagonal,
  watermarkHeader,
  watermarkFooter,
  watermarkFullName,
  watermarkEmail,
  watermarkIpAddress,
}: DataRoomNode) => ({
  fileSecurity,
  expirationDays,
  blurProtection,
  watermarkDiagonal,
  watermarkHeader,
  watermarkFooter,
  watermarkFullName,
  watermarkEmail,
  watermarkIpAddress,
});

export const getListingTypeLabel = (listingType: ListingType) =>
  listingType === LISTING_TYPE.URGENT ? 'urgent' : 'profitable';

export const formatMemoryLabel = (memory: number | string, suffix = 'GB') =>
  `${memory} ${suffix}`;

export const getTotalFilesSize = (files: File[]) =>
  files.reduce((sum, file) => sum + (file?.size || 0), 0);

export const getIsNotificationUnread = (
  notificationStatus: NotificationsStatus,
) => notificationStatus === NOTIFICATION_STATUS.UNREAD;

export const getIsNotificationForLeads = (notificationsBy: NotificationBy) =>
  notificationsBy === NOTIFICATION_BY.FOR_YOUR_LEADS;

export const getResetedPageParams = (
  pageParam: string | string[] = PAGE_PARAM_KEYWORD,
) => {
  if (Array.isArray(pageParam)) {
    return pageParam.reduce((acc, key) => ({ ...acc, [key]: FIRST_PAGE }), {});
  }

  return {
    [pageParam]: FIRST_PAGE,
  };
};

export const isValueEmpty = (value: any) => {
  if (!value) return true;

  if (Array.isArray(value)) return !value.length;

  if (typeof value === 'object') return !Object.keys(value).length;

  return false;
};

export const sortDealTopNodes = (nodes: DealRoomTopNodes[]) =>
  (nodes || []).sort(({ orderNumber: a }, { orderNumber: b }) =>
    getSortComparator(a, b),
  );

export function swapElementsInArray<T>(array: T[], currentKey: T, nextKey: T) {
  return array.map((el) => {
    if (el === currentKey) return nextKey;
    if (el === nextKey) return currentKey;

    return el;
  });
}

export function replaceOrSwapInArray<T>(array: T[], currentKey: T, nextKey: T) {
  return (
    array.includes(nextKey) ? swapElementsInArray : replaceElementInArray
  )(array, currentKey, nextKey);
}

export function swapObjectColumns<T extends Record<string, any>>(
  object: T,
  currentKey: keyof T,
  nextKey: keyof T,
) {
  if (currentKey === nextKey) return object;

  return {
    ...object,
    [currentKey]: object[nextKey],
    [nextKey]: object[currentKey],
  };
}

export function replaceOrSwapInObject<T extends Record<string, any>>(
  object: T,
  currentKey: keyof T,
  nextKey: keyof T,
) {
  return (
    Object.keys(object).includes(String(nextKey))
      ? swapObjectColumns
      : updateKeyInObject
  )(object, currentKey, nextKey);
}

export const getQASide = (isBuyer: boolean) =>
  isBuyer ? Q_AND_A_SIDES.BUY : Q_AND_A_SIDES.SELL;

export const sortOptions = (options: Option[] = []) =>
  [...options].sort((a, b) => getSortComparator(a.label, b.label));

export const getCanUseBrowserDOM = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

export function getNextPageParam<T>({
  currentPage,
  totalPages,
}: PaginatedData<T>) {
  if (currentPage < totalPages) return currentPage + 1;

  return undefined;
}

export const getIsFileReorderViewModeFile = (
  fileReorderViewMode: FileReorderViewMode,
) => fileReorderViewMode === FILE_REORDER_VIEW_MODE.FILE;

export const getIsFileReorderViewModeLead = (
  fileReorderViewMode: FileReorderViewMode,
) => fileReorderViewMode === FILE_REORDER_VIEW_MODE.LEAD;

export const getIsNotNewFeature = (createdAt?: Date | string) =>
  new Date(createdAt) <= new Date() &&
  !isDateFromLastDays(createdAt, DISCOVERING_FEATURE_VISIBILITY_DAYS);

export const hasNotSeenDiscoveryFeature = (
  seenFeature: boolean,
  createdAt?: Date | string,
) => !seenFeature && !getIsNotNewFeature(createdAt);

export const addMissingRequiredFields = (
  required: string[],
  data: Record<string, any>,
) =>
  required.reduce(
    (acc, column) => ({
      ...acc,
      [column]: acc[column] ?? '',
    }),
    data,
  );

export const getApiAutorization = (token: string) => `Bearer ${token}`;

export const getShowGroupChat = (
  activeChatSessionId: string,
  buyGroupDataRoomMembers: DataRoomMember[],
) => !!activeChatSessionId && getArrayLength(buyGroupDataRoomMembers) > 1;

export const normalizeText = (value = '') => value.trim().toLowerCase();

export const normalizeEmail = (email = '') =>
  normalizeText(email).replace(/\s+/g, '');

export const getPageUrl = () => window.location.href;

export const trimSearchString = (value: string): string =>
  (value || '').trim().replaceAll(/\s+/g, ' ');

export const sortByName = <T extends { name: string }>(
  items: T[] = EMPTY_ARRAY,
) =>
  [...items].sort((a, b) =>
    getSortComparator(a.name.toLowerCase(), b.name.toLowerCase()),
  );

export const isOdd = (n: number) => n % 2 === 1;

export const isEven = (n: number) => n % 2 === 0;

export const getHasActiveFilters = (
  filterOptions: FilterOption[],
  params: Record<string, unknown>,
) => filterOptions.some(({ value }) => !!params[value]);

export const getSortedOptionsFromStrings = (values: string[] = []) =>
  sortOptions(values.map((value) => ({ value, label: value })));

export const getFilterOptionByValue = (
  filterOptions: FilterOption[],
  filterValue: string,
) => filterOptions?.find(({ value }) => value === filterValue);

export const getIsEditableElement = (element: EventTarget | null) =>
  element instanceof HTMLElement &&
  (element.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName));

export const getJwtLifetime = (token: string): number | undefined => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp, iat } = JSON.parse(window.atob(payload));

    return exp && iat ? (exp - iat) * 1000 : undefined;
  } catch (e) {
    return undefined;
  }
};

export const getUniqueId = () =>
  (window.crypto as Crypto & { randomUUID?: () => string })?.randomUUID?.() ||
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const getSafeInternalPath = (path?: string | null) =>
  path?.startsWith('/') && !path.startsWith('//') ? path : undefined;

export const getDigitsOnly = (value: string) =>
  (value || '').replace(/\D/g, '');

export const formatSecondsAsClock = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${`0${seconds % 60}`.slice(-2)}`;

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const getLowercaseFirstLetter = (value = '') =>
  value.charAt(0).toLowerCase() + value.slice(1);

export default {
  mapToMoneyFormat,
  noop,
  humanize,
  renderIcon,
  shortPastTime,
  toDateFormat,
  checkForZero,
  getDotedNumber,
  getIntegerFromDottedNumber,
  getShortenedString,
  changePropertyValueWith,
  multiSelectValueToStringArray,
  mapArrayToParams,
  mapParamsToArray,
  stringifyFilterValues,
  createSlug,
  millisecondsPassed,
  composeBusinessUrl,
  humanizeSeconds,
  getDateVariables,
  formatAskingPrice,
  getAskingPrice,
  appendUrlParams,
  sentryError,
  areParamsEqual,
  isSSGBot,
  isSSGHuman,
  isSSG,
  replaceElementInArray,
  replaceElementInStringArray,
  downloadUrl,
  getDateInUserTimezone,
  getTimeFromUserTimezone,
  timeSpentInSecondsHumanize,
  humanizeFileSize,
  isObject,
  generateNumberOfYears,
  getLinkedinUrlAndVanityName,
  transformCurrencyPropertiesToNumber,
  transformPriceNumberToCurrencyString,
  getEmailPhoneInputType,
  replaceNullishValues,
  getElapsedTimeInSecs,
};
